<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    min-width="auto"
  >
    <template #activator="{ on, attrs }">
      <v-text-field
        :value="label"
        :label="fieldLabel"
        :rules="rule"
        prepend-inner-icon="mdi-calendar"
        readonly
        v-bind="attrs"
        v-on="on"
      />
    </template>

    <v-card class="d-flex" outlined>
      <v-list dense class="py-0" style="min-width: 190px">
        <v-list-item
          v-for="preset in availablePresets"
          :key="preset.id"
          :class="{ 'v-list-item--active': preset.id === activePreset }"
          @click="applyPreset(preset)"
        >
          <v-list-item-title>{{ preset.name }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <v-divider vertical />

      <v-date-picker
        v-model="range"
        range
        :allowed-dates="allowedDates"
        locale="ru-RU"
        :first-day-of-week="1"
        no-title
        @change="onPick"
      />
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: 'PeriodRangeSelector',

  props: {
    value: {
      type: Array,
      required: true,
    },
    fieldLabel: {
      type: String,
      default: 'Период',
    },
    noRules: Boolean,
    singleMonth: Boolean,
    maxDays: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      menu: false,
      activePreset: null,
      presets: [
        { id: 'today', name: 'Сегодня' },
        { id: 'yesterday', name: 'Вчера' },
        { id: 'currWeek', name: 'Текущая неделя' },
        { id: 'prevWeek', name: 'Предыдущая неделя' },
        { id: 'currMonth', name: 'Текущий месяц' },
        { id: 'prevMonth', name: 'Предыдущий месяц' },
        { id: 'currQuarter', name: 'Текущий квартал' },
        { id: 'currYear', name: 'Текущий год' },
      ],
      rule: [
        () => {
          if (this.singleMonth && this.isCrossMonth(this.value)) return 'Период должен быть в пределах одного месяца'
          if (this.isTooLong(this.value)) return `Период не может быть больше ${this.maxDays} дн.`
          if (this.noRules) return true
          return (this.value.length === 2 && !this.value.includes(null)) || 'Данное поле обязательно'
        },
      ],
    }
  },

  computed: {
    range: {
      get() {
        return this.value
      },
      set(range) {
        this.$emit('input', [...range].sort())
      },
    },

    availablePresets() {
      if (!this.singleMonth && !this.maxDays) return this.presets
      return this.presets.filter(preset => {
        const range = this.resolvePreset(preset.id).map(this.toString)
        if (this.singleMonth && this.isCrossMonth(range)) return false
        return !this.isTooLong(range)
      })
    },

    allowedDates() {
      if ((!this.singleMonth && !this.maxDays) || this.value.length !== 1) return null
      const picked = this.value[0]
      return date => {
        if (this.singleMonth && date.slice(0, 7) !== picked.slice(0, 7)) return false
        return !this.isTooLong([picked, date].sort())
      }
    },

    label() {
      if (this.value.length === 1) return this.format(this.value[0])
      if (this.value.length !== 2 || this.value.includes(null)) return ''
      if (this.value[0] === this.value[1]) return this.format(this.value[0])
      return `${this.format(this.value[0])} — ${this.format(this.value[1])}`
    },
  },

  watch: {
    menu(opened) {
      if (!opened && this.value.length === 1) {
        this.activePreset = null
        this.$emit('input', [this.value[0], this.value[0]])
      }
    },
  },

  methods: {
    isCrossMonth(range) {
      return range.length === 2 && !!range[0] && !!range[1] && range[0].slice(0, 7) !== range[1].slice(0, 7)
    },

    isTooLong(range) {
      if (!this.maxDays || range.length !== 2 || !range[0] || !range[1]) return false
      const days = Math.round((Date.parse(range[1]) - Date.parse(range[0])) / 86400000) + 1
      return days > this.maxDays
    },

    format(date) {
      if (!date) return ''
      const [year, month, day] = date.split('-')
      return `${day}.${month}.${year}`
    },

    toString(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    shift(date, days) {
      const result = new Date(date)
      result.setDate(result.getDate() + days)
      return result
    },

    startOfWeek(date) {
      const result = new Date(date)
      const weekDay = (result.getDay() + 6) % 7
      result.setDate(result.getDate() - weekDay)
      return result
    },

    resolvePreset(id) {
      const now = new Date()

      switch (id) {
        case 'today':
          return [now, now]
        case 'yesterday': {
          const yesterday = this.shift(now, -1)
          return [yesterday, yesterday]
        }
        case 'currWeek': {
          const from = this.startOfWeek(now)
          return [from, this.shift(from, 6)]
        }
        case 'prevWeek': {
          const from = this.shift(this.startOfWeek(now), -7)
          return [from, this.shift(from, 6)]
        }
        case 'currMonth':
          return [
            new Date(now.getFullYear(), now.getMonth(), 1),
            new Date(now.getFullYear(), now.getMonth() + 1, 0),
          ]
        case 'prevMonth':
          return [
            new Date(now.getFullYear(), now.getMonth() - 1, 1),
            new Date(now.getFullYear(), now.getMonth(), 0),
          ]
        case 'currQuarter': {
          const quarter = Math.floor(now.getMonth() / 3)
          return [
            new Date(now.getFullYear(), quarter * 3, 1),
            new Date(now.getFullYear(), quarter * 3 + 3, 0),
          ]
        }
        case 'currYear':
          return [new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31)]
        default:
          return [now, now]
      }
    },

    applyPreset(preset) {
      const [from, to] = this.resolvePreset(preset.id)
      this.activePreset = preset.id
      this.$emit('input', [this.toString(from), this.toString(to)])
      this.menu = false
    },

    onPick(range) {
      if (range.length === 2) {
        this.activePreset = null
        this.menu = false
      }
    },
  },
}
</script>
